import React from 'react';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Close, HelpOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { PAGE_TOUR_STEPS, resolvePageTourKey } from '../../config/tourSteps';
import { colors } from '../../design-system';

// Keep this export aligned with the screens that can launch a page tour.
export const HELP_PAGE_KEYS = Object.keys(PAGE_TOUR_STEPS);

export const resolveHelpPageKey = (pathname, currentPage) =>
  currentPage || resolvePageTourKey(pathname) || 'default';

const HelpModal = ({
  open,
  onClose,
  onExited,
  onStartTour,
  currentPage,
  hasPageTour,
}) => {
  const { t } = useTranslation('onboarding');
  const pageKey = currentPage || 'default';
  const pageTitle = t(`helpModal.pageDescriptions.${pageKey}.title`);
  const pageDescription = t(`helpModal.pageDescriptions.${pageKey}.description`);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      TransitionProps={{ onExited }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: 'background.paper',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      {/* Green accent bar */}
      <Box sx={{ height: 6, backgroundColor: colors.primary[700] }} />

      {/* Header row */}
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <HelpOutline sx={{ fontSize: 20, color: colors.primary[700] }} />
          <Typography
            sx={{
              fontSize: '0.95rem',
              fontWeight: 700,
              color: colors.primary[900],
            }}
          >
            {pageTitle}
          </Typography>
        </Stack>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label={t('helpModal.actions.close')}
          sx={{ color: colors.neutral[400], p: 0.5 }}
        >
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Divider */}
      <Box sx={{ mx: 2.5, mt: 1.5, borderBottom: '1px solid', borderColor: 'divider' }} />

      {/* Description */}
      <Box sx={{ px: 2.5, pt: 2, pb: hasPageTour ? 2 : 2.5 }}>
        <Typography
          sx={{
            fontSize: '0.875rem',
            lineHeight: 1.7,
            color: 'text.secondary',
          }}
        >
          {pageDescription}
        </Typography>
      </Box>

      {/* Tour button */}
      {hasPageTour && (
        <Box sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            variant="contained"
            onClick={onStartTour}
            fullWidth
            disableElevation
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '0.9rem',
              fontWeight: 700,
              py: 1.2,
              backgroundColor: colors.primary[700],
              color: colors.neutral.white,
              '&:hover': { backgroundColor: colors.primary[800] },
            }}
          >
            {t('helpModal.actions.startTour')}
          </Button>
        </Box>
      )}
    </Dialog>
  );
};

export default HelpModal;
